/**
 * Utilities for temporarily neutralizing ancestor CSS properties that prevent
 * `position: fixed` from working relative to the viewport.
 *
 * Used by the video fullscreen feature to CSS-promote the iframe without
 * creating a new browsing context (which would break audio continuity).
 */

// Properties that create a containing block for `position: fixed`
const BLOCKING_PROPS = [
  'transform',
  'filter',
  'perspective',
  'will-change',
  'contain',
  'backdrop-filter',
];

export interface SavedStyle {
  element: HTMLElement;
  properties: Record<string, string>;
}

/**
 * Walk from `start` up to `<body>`, neutralizing any CSS property that would
 * create a containing block for `position: fixed`.
 *
 * Also handles CSS animations with `forwards` fill — the fill can re-introduce
 * a transform even after we clear the inline style, so we remove the animation
 * and freeze the current visual opacity to prevent a flash.
 */
export function neutralizeAncestors(start: HTMLElement): SavedStyle[] {
  const saved: SavedStyle[] = [];
  const ancestors: HTMLElement[] = [];
  let el: HTMLElement | null = start.parentElement;

  while (el && el !== document.body) {
    ancestors.push(el);
    el = el.parentElement;
  }

  // Pass 1: Kill all transitions so subsequent style changes are instant
  for (const ancestor of ancestors) {
    const hasTransition =
      getComputedStyle(ancestor).getPropertyValue('transition-property') !== 'none';
    if (hasTransition) ancestor.style.setProperty('transition', 'none', 'important');
  }
  void start.offsetHeight; // Force reflow — transitions are now disabled

  // Pass 2: Neutralize transforms, animations, z-index
  for (const ancestor of ancestors) {
    const cs = getComputedStyle(ancestor);
    const props: Record<string, string> = {};

    if (ancestor.style.getPropertyValue('transition') === 'none') {
      props.transition = '';
    }

    const anim = cs.getPropertyValue('animation-name');
    if (anim && anim !== 'none') {
      props.animation = ancestor.style.getPropertyValue('animation');
      props.opacity = ancestor.style.getPropertyValue('opacity');
      ancestor.style.setProperty('opacity', cs.getPropertyValue('opacity'));
      ancestor.style.setProperty('animation', 'none', 'important');
    }

    for (const prop of BLOCKING_PROPS) {
      const val = cs.getPropertyValue(prop);
      if (val && val !== 'none' && val !== 'auto' && val !== 'normal') {
        props[prop] = ancestor.style.getPropertyValue(prop);
        ancestor.style.setProperty(prop, 'none', 'important');
      }
    }

    const zIndex = cs.getPropertyValue('z-index');
    if (zIndex !== 'auto') {
      props['z-index'] = ancestor.style.getPropertyValue('z-index');
      ancestor.style.setProperty('z-index', 'auto', 'important');
    }

    if (Object.keys(props).length > 0) saved.push({ element: ancestor, properties: props });
  }
  return saved;
}

export function restoreAncestors(saved: SavedStyle[]) {
  for (const { element, properties } of saved) {
    const hadAnimation = 'animation' in properties;

    for (const [prop, value] of Object.entries(properties)) {
      if (prop === 'transition') continue;
      if (prop === 'animation') {
        element.style.setProperty('animation', 'none');
        continue;
      }
      if (prop === 'opacity' && hadAnimation) continue;

      if (value) element.style.setProperty(prop, value);
      else element.style.removeProperty(prop);
    }
  }

  requestAnimationFrame(() => {
    for (const { element, properties } of saved) {
      if ('transition' in properties) element.style.removeProperty('transition');
    }
  });
}
